import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canManageConsumables, hasCapability, isAdminRole, formatINR, formatDate, formatDateTime } from "@/lib/permissions";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PackagePlus, Search, PlusCircle, MinusCircle, History, AlertTriangle, Boxes, Settings2, Trash2, Download, Upload, FileSpreadsheet, ArrowDownToLine, ArrowUpFromLine, X as XIcon, Building2, ArrowLeftRight, Check, XCircle, Pencil } from "lucide-react";
import { useBranches, branchColor } from "@/hooks/useBranches";

const UOM = ["nos", "pcs", "kg", "gm", "ltr", "ml", "mtr", "cm", "box", "pack", "roll", "rolls", "pairs", "cone"];

function useConsumableCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    try {
      const { data } = await api.get("/consumables/categories");
      setCats(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);
  return { cats, loading, reload };
}

export default function Consumables() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [txnDialog, setTxnDialog] = useState(null); // "in" | "out" | null
  const [branchFilter, setBranchFilter] = useState("all");   // branch id or "all"
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [transfersOpen, setTransfersOpen] = useState(false);
  const [txfrDialog, setTxfrDialog] = useState(null);    // { consumable, mode: 'new' }
  const { cats, reload: reloadCats } = useConsumableCategories();
  const { branches, reload: reloadBranches } = useBranches();
  const isAdmin = isAdminRole(user?.role);

  // Feb 2026 — granular consumables permissions. The old monolithic
  // `manage_consumables` role check has been split into three independent
  // capabilities so a Purchase Manager can (for example) create & edit items
  // but not adjust physical stock, and a Store Keeper can adjust stock without
  // being able to create or edit items.
  const canCreate = hasCapability(user, "create_consumable");
  const canEdit = hasCapability(user, "edit_consumable_details");
  const canAdjust = hasCapability(user, "adjust_consumable_stock");
  const canManage = canCreate || canEdit || canAdjust; // any manage capability

  const load = async () => {
    try {
      const params = { include_unassigned: isAdmin };
      const { data } = await api.get("/consumables", { params });
      setItems(data);
      if (isAdmin) {
        try {
          const { data: uc } = await api.get("/consumables/unassigned-count");
          setUnassignedCount(uc?.count || 0);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error("Failed to load consumables");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((c) => {
      if (branchFilter === "unassigned") {
        if (c.branch_id) return false;
      } else if (branchFilter !== "all") {
        if (c.branch_id !== branchFilter) return false;
      }
      if (catFilter !== "all" && c.category !== catFilter) return false;
      if (query && !`${c.code} ${c.name} ${c.vendor || ""}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, q, catFilter, branchFilter]);

  const belowReorder = filtered.filter((c) => c.reorder_level != null && c.current_stock <= c.reorder_level).length;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/consumables/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Consumables_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download inventory");
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="consumables-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Stores</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Consumables</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {items.length} items
            {belowReorder > 0 && (
              <span className="ml-3 text-amber-700 font-medium inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {belowReorder} below reorder level
              </span>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {canAdjust && (
              <>
                <Button
                  onClick={() => setTxnDialog("in")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="quick-receive-btn"
                >
                  <ArrowDownToLine className="w-4 h-4 mr-2" /> Receive Stock
                </Button>
                <Button
                  onClick={() => setTxnDialog("out")}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="quick-issue-btn"
                >
                  <ArrowUpFromLine className="w-4 h-4 mr-2" /> Issue Stock
                </Button>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = "/consumables/ledger")}
                  data-testid="open-ledger-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Issue / Receive Ledger
                </Button>
              </>
            )}
            <Button variant="outline" onClick={exportExcel} disabled={exporting} data-testid="export-consumables-btn">
              <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting..." : "Export Excel"}
            </Button>
            {canCreate && (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="upload-opening-stock-btn">
                  <Upload className="w-4 h-4 mr-2" /> Upload Opening Stock
                </Button>
                <Button variant="outline" onClick={() => setManageCatsOpen(true)} data-testid="manage-categories-btn">
                  <Settings2 className="w-4 h-4 mr-2" /> Categories
                </Button>
                <Button onClick={() => setAddOpen(true)} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-consumable-btn">
                  <PackagePlus className="w-4 h-4 mr-2" /> Add Consumable
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Branch tabs — Feb 2026 */}
      {branches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="branch-tabs">
          <button
            onClick={() => setBranchFilter("all")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${branchFilter === "all" ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
            data-testid="branch-tab-all"
          >
            All Branches <span className="ml-1 text-[10px] opacity-75">({items.filter(i => i.branch_id).length})</span>
          </button>
          {branches.map((b, idx) => {
            const active = branchFilter === b.id;
            const count = items.filter((c) => c.branch_id === b.id).length;
            const clr = branchColor(idx);
            return (
              <button
                key={b.id}
                onClick={() => setBranchFilter(b.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition inline-flex items-center gap-1.5 ${active ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : `bg-white ${clr.fg} border-slate-200 hover:border-[#7C1D3A]`}`}
                data-testid={`branch-tab-${b.code}`}
              >
                <Building2 className="w-3.5 h-3.5" /> {b.name}
                <span className="text-[10px] opacity-75">({count})</span>
              </button>
            );
          })}
          {isAdmin && unassignedCount > 0 && (
            <button
              onClick={() => setBranchFilter("unassigned")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition inline-flex items-center gap-1.5 ${branchFilter === "unassigned" ? "bg-amber-600 text-white border-amber-600" : "bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-500"}`}
              data-testid="branch-tab-unassigned"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Unassigned
              <span className="text-[10px] opacity-75">({unassignedCount})</span>
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTransfersOpen(true)}
                data-testid="branch-transfers-btn"
              >
                <ArrowLeftRight className="w-4 h-4 mr-1.5" /> Branch Transfers
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Migration banner — admins only, when there are unassigned items */}
      {isAdmin && unassignedCount > 0 && (
        <Card className="bg-amber-50 border-amber-200" data-testid="migration-banner">
          <CardContent className="p-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-amber-900 text-sm">{unassignedCount} consumable{unassignedCount === 1 ? "" : "s"} not yet assigned to a branch</div>
                <p className="text-xs text-amber-800 mt-1">These items are only visible to administrators. Users without branch privileges cannot see them until you assign each to a branch. Bulk-assign now, or edit individual items from the row.</p>
              </div>
            </div>
            <Button onClick={() => setMigrationOpen(true)} className="bg-amber-600 hover:bg-amber-700 shrink-0" data-testid="open-migration-dialog-btn">
              Bulk-assign now
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search code, name, vendor..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="consumables-search" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-right">Reorder Level</th>
                <th className="px-4 py-3 text-right">Unit Cost</th>
                <th className="px-4 py-3">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <Boxes className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <div className="text-slate-500">No consumables yet</div>
                </td></tr>
              ) : filtered.map((c) => {
                const low = c.reorder_level != null && c.current_stock <= c.reorder_level;
                const branchIdx = branches.findIndex((b) => b.id === c.branch_id);
                const branch = branchIdx >= 0 ? branches[branchIdx] : null;
                const clr = branchIdx >= 0 ? branchColor(branchIdx) : null;
                return (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(c)} data-testid={`consumable-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">{c.code}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{c.category}</Badge></td>
                    <td className="px-4 py-3">
                      {branch ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${clr.bg} ${clr.fg} ${clr.border}`} data-testid={`branch-chip-${c.code}`}>
                          <Building2 className="w-3 h-3" /> {branch.name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-amber-50 text-amber-800 border-amber-200" data-testid={`branch-chip-none-${c.code}`}>
                          <AlertTriangle className="w-3 h-3" /> Unassigned
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${low ? "text-amber-700" : "text-slate-900"}`}>
                      {c.current_stock} {c.unit_of_measure}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 font-mono">{c.reorder_level ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.unit_cost ? formatINR(c.unit_cost) : "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.location || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {addOpen && <AddConsumableDialog cats={cats} branches={branches} defaultBranchId={branchFilter !== "all" && branchFilter !== "unassigned" ? branchFilter : null} open={addOpen} onOpenChange={setAddOpen} onDone={() => { setAddOpen(false); load(); }} />}
      {detail && <ConsumableDetailDialog item={detail} branches={branches} onClose={() => setDetail(null)} onChanged={load} canEdit={canEdit} canAdjust={canAdjust} onTransfer={(item) => { setTxfrDialog({ consumable: item }); setDetail(null); }} />}
      {manageCatsOpen && <ManageCategoriesDialog open={manageCatsOpen} onOpenChange={setManageCatsOpen} cats={cats} onChanged={reloadCats} />}
      {bulkOpen && <BulkOpeningStockDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={() => { setBulkOpen(false); load(); }} />}
      {migrationOpen && (
        <BulkAssignBranchDialog
          branches={branches}
          unassignedCount={unassignedCount}
          onClose={() => setMigrationOpen(false)}
          onDone={() => { setMigrationOpen(false); load(); }}
        />
      )}
      {transfersOpen && (
        <BranchTransfersDialog
          branches={branches}
          items={items}
          onClose={() => setTransfersOpen(false)}
          onDone={() => load()}
        />
      )}
      {txfrDialog && (
        <NewBranchTransferDialog
          consumable={txfrDialog.consumable}
          branches={branches}
          onClose={() => setTxfrDialog(null)}
          onDone={() => { setTxfrDialog(null); load(); }}
        />
      )}
      {txnDialog && (
        <IssueReceiveDialog
          mode={txnDialog}
          items={items}
          onClose={() => setTxnDialog(null)}
          onDone={() => { setTxnDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function BulkOpeningStockDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async (format) => {
    const token = localStorage.getItem("assetiq_token");
    try {
      const res = await fetch(`${API_BASE}/consumables/bulk/template?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `consumables_opening_stock_template.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/consumables/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Processed ${data.total_rows} rows · ${data.created} new · ${data.updated} updated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-consumables-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#7C1D3A]" /> Upload Opening Consumable Stock
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="p-4 bg-[#fcf0f3] border border-[#f5c4d3] rounded-lg text-sm">
              <div className="font-semibold text-[#7C1D3A] mb-1">1. Download the template</div>
              <p className="text-xs text-slate-600 mb-2">
                Required column: <b>name</b>. Others (category, UOM, current_stock, reorder_level, unit_cost, vendor, location, description) are optional. If a consumable with the same name already exists, its stock is <b>added</b> to and a transaction is logged.
              </p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("xlsx")} data-testid="download-cons-xlsx-template">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Excel Template
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("csv")} data-testid="download-cons-csv-template">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV Template
                </Button>
              </div>
            </div>

            <div className="p-4 border border-dashed border-slate-300 rounded-lg text-sm">
              <div className="font-semibold text-slate-700 mb-2">2. Upload your filled file</div>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-cons-file-input"
                className="cursor-pointer"
              />
              {file && (
                <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={!file || uploading} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="bulk-cons-upload-submit">
                {uploading ? "Uploading..." : "Import Opening Stock"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="bulk-cons-result">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                <div className="text-xs text-emerald-700">Newly created</div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
                <div className="text-xs text-blue-700">Stock added to existing</div>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                <div className="text-xs text-red-700">Errors</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-48 overflow-y-auto border rounded p-2 text-xs">
                {result.errors.map((e, i) => (
                  <div key={`err-${e.row}-${i}`} className="text-red-700 py-0.5">
                    Row {e.row} ({e.name || "?"}): {e.error}
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={onDone} className="bg-[#7C1D3A] hover:bg-[#6c1c36]">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddConsumableDialog({ cats, branches, defaultBranchId, open, onOpenChange, onDone }) {
  const [form, setForm] = useState({
    name: "",
    category: cats[0]?.name || "Other",
    unit_of_measure: cats[0]?.default_uom || "pcs",
    current_stock: 0, reorder_level: "", unit_cost: "",
    vendor: "", location: "", description: "",
    branch_id: defaultBranchId || branches.find((b) => b.is_default)?.id || branches[0]?.id || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When category changes, auto-fill default UOM
  const onCategoryChange = (name) => {
    const cat = cats.find((c) => c.name === name);
    setForm((f) => ({ ...f, category: name, unit_of_measure: cat?.default_uom || f.unit_of_measure }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (branches.length > 0 && !form.branch_id) {
      toast.error("Please pick a branch");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consumables", {
        ...form,
        current_stock: Number(form.current_stock) || 0,
        reorder_level: form.reorder_level === "" ? null : Number(form.reorder_level),
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      });
      toast.success("Consumable added");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">New Consumable</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-consumable-name" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={onCategoryChange}>
              <SelectTrigger data-testid="new-consumable-category"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Unit of Measure</Label>
            <Select value={form.unit_of_measure} onValueChange={(v) => set("unit_of_measure", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UOM.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {branches.length > 0 && (
            <div className="col-span-2">
              <Label>Branch <span className="text-red-500">*</span></Label>
              <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}>
                <SelectTrigger data-testid="new-consumable-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Opening Stock</Label><Input type="number" value={form.current_stock} onChange={(e) => set("current_stock", e.target.value)} /></div>
          <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)} placeholder="Alert when stock ≤ this" /></div>
          <div><Label>Unit Cost (₹)</Label><Input type="number" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} /></div>
          <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
          <div className="col-span-2"><Label>Storage Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Stores Rack A-3" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-consumable-btn">
              {submitting ? "Saving..." : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConsumableDetailDialog({ item, branches = [], onClose, onChanged, canEdit, canAdjust, onTransfer }) {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const { cats } = useConsumableCategories();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [adjustDir, setAdjustDir] = useState("out");
  const [form, setForm] = useState({ qty: 1, reason: "", linked_asset_id: "" });
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(item.name || "");
  const [renaming, setRenaming] = useState(false);
  // Full edit-details form (Feb 2026). Non-admins can edit everything except name.
  const [editForm, setEditForm] = useState({
    category: item.category || "Other",
    unit_of_measure: item.unit_of_measure || "pcs",
    reorder_level: item.reorder_level ?? "",
    unit_cost: item.unit_cost ?? "",
    vendor: item.vendor || "",
    location: item.location || "",
    description: item.description || "",
    branch_id: item.branch_id || "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const setEF = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const saveDetails = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        category: editForm.category,
        unit_of_measure: editForm.unit_of_measure,
        reorder_level: editForm.reorder_level === "" ? null : Number(editForm.reorder_level),
        unit_cost: editForm.unit_cost === "" ? null : Number(editForm.unit_cost),
        vendor: editForm.vendor || null,
        location: editForm.location || null,
        description: editForm.description || null,
        branch_id: editForm.branch_id || null,
      };
      // Drop unchanged values so the API only sees actual edits.
      Object.keys(payload).forEach((k) => {
        const orig = item[k] ?? null;
        const next = payload[k] ?? null;
        if (`${orig}` === `${next}`) delete payload[k];
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setSavingEdit(false);
        return;
      }
      await api.patch(`/consumables/${item.id}`, payload);
      toast.success("Details updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingEdit(false); }
  };

  const saveName = async () => {
    const next = (nameDraft || "").trim();
    if (!next || next === item.name) { setEditingName(false); return; }
    setRenaming(true);
    try {
      await api.patch(`/consumables/${item.id}`, { name: next });
      toast.success("Name updated");
      setEditingName(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to rename");
    } finally { setRenaming(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/consumables/${item.id}/transactions`);
      setTxns(data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/assets").then(({ data }) => setAssets(data)).catch(() => {});
    // eslint-disable-next-line
  }, [item.id]);

  const adjust = async () => {
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    if (Number(form.qty) <= 0) { toast.error("Quantity must be > 0"); return; }
    setBusy(true);
    try {
      await api.post(`/consumables/${item.id}/adjust`, {
        direction: adjustDir,
        qty: Number(form.qty),
        reason: form.reason,
        linked_asset_id: form.linked_asset_id || null,
      });
      toast.success(adjustDir === "in" ? "Stock added" : "Stock issued");
      setForm({ qty: 1, reason: "", linked_asset_id: "" });
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Adjustment failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{item.code}</span>
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                  className="h-8 max-w-md"
                  data-testid="consumable-name-input"
                />
                <Button size="sm" onClick={saveName} disabled={renaming} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="consumable-name-save">
                  <Check className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingName(false); setNameDraft(item.name); }} className="h-8">
                  <XIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <span data-testid="consumable-name-label">{item.name}</span>
                {isAdmin && (
                  <button
                    onClick={() => setEditingName(true)}
                    className="text-slate-400 hover:text-[#7C1D3A] p-1 rounded"
                    title="Rename (admin only)"
                    data-testid="consumable-name-edit-btn"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
            {item.branch_id && (() => {
              const idx = branches.findIndex((b) => b.id === item.branch_id);
              const b = idx >= 0 ? branches[idx] : null;
              const clr = idx >= 0 ? branchColor(idx) : null;
              return b ? (
                <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${clr.bg} ${clr.fg} ${clr.border}`}>
                  <Building2 className="w-3 h-3" /> {b.name}
                </span>
              ) : null;
            })()}
            {canEdit && item.branch_id && branches.length > 1 && onTransfer && (
              <Button
                onClick={() => onTransfer(item)}
                variant="outline"
                size="sm"
                className="ml-auto"
                data-testid="send-to-branch-btn"
              >
                <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Send to another branch
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-slate-500">Current Stock</div><div className="font-heading text-2xl font-bold text-[#7C1D3A]">{item.current_stock}</div><div className="text-xs text-slate-500">{item.unit_of_measure}</div></div>
          <div><div className="text-xs text-slate-500">Reorder Level</div><div className="text-lg font-mono">{item.reorder_level ?? "—"}</div></div>
          <div><div className="text-xs text-slate-500">Unit Cost</div><div className="text-lg font-mono">{item.unit_cost ? formatINR(item.unit_cost) : "—"}</div></div>
          <div><div className="text-xs text-slate-500">Location</div><div>{item.location || "—"}</div></div>
        </div>

        <Tabs defaultValue={canAdjust ? "adjust" : (canEdit ? "details" : "history")} className="mt-4">
          <TabsList>
            {canAdjust && <TabsTrigger value="adjust" data-testid="tab-adjust">Adjust Stock</TabsTrigger>}
            {canEdit && <TabsTrigger value="details" data-testid="tab-details">Edit Details</TabsTrigger>}
            <TabsTrigger value="history" data-testid="tab-history">Transaction History</TabsTrigger>
          </TabsList>

          {canAdjust && (
            <TabsContent value="adjust" className="space-y-3 mt-4">
              <div className="flex gap-2">
                <Button variant={adjustDir === "in" ? "default" : "outline"} onClick={() => setAdjustDir("in")} className={adjustDir === "in" ? "bg-emerald-600 hover:bg-emerald-700" : ""} data-testid="adjust-in-btn">
                  <PlusCircle className="w-4 h-4 mr-2" /> Add Stock (IN)
                </Button>
                <Button variant={adjustDir === "out" ? "default" : "outline"} onClick={() => setAdjustDir("out")} className={adjustDir === "out" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="adjust-out-btn">
                  <MinusCircle className="w-4 h-4 mr-2" /> Issue (OUT)
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantity *</Label><Input type="number" min="0" step="0.01" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="adjust-qty" /></div>
                <div>
                  <Label>{adjustDir === "out" ? "Issued to Asset (optional)" : "Linked to Asset (optional)"}</Label>
                  <Select value={form.linked_asset_id || "none"} onValueChange={(v) => setForm({ ...form, linked_asset_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="none">— None —</SelectItem>
                      {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_id} — {a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Reason *</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={adjustDir === "in" ? "e.g. Received against PO SPR/25-26/0002" : "e.g. Consumed during machine cleaning"} data-testid="adjust-reason" /></div>
              </div>
              <div className="flex justify-end">
                <Button onClick={adjust} disabled={busy} className={adjustDir === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} data-testid="confirm-adjust-btn">
                  {busy ? "Processing..." : (adjustDir === "in" ? "Add to Stock" : "Issue")}
                </Button>
              </div>
            </TabsContent>
          )}

          {canEdit && (
            <TabsContent value="details" className="space-y-3 mt-4" data-testid="details-form">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label>Category</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEF("category", v)}>
                    <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <Select value={editForm.unit_of_measure} onValueChange={(v) => setEF("unit_of_measure", v)}>
                    <SelectTrigger data-testid="edit-uom"><SelectValue /></SelectTrigger>
                    <SelectContent>{UOM.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reorder Level</Label>
                  <Input type="number" value={editForm.reorder_level} onChange={(e) => setEF("reorder_level", e.target.value)} placeholder="Alert when ≤ this" data-testid="edit-reorder" />
                </div>
                <div>
                  <Label>Unit Cost (₹)</Label>
                  <Input type="number" value={editForm.unit_cost} onChange={(e) => setEF("unit_cost", e.target.value)} data-testid="edit-unit-cost" />
                </div>
                <div>
                  <Label>Vendor</Label>
                  <Input value={editForm.vendor} onChange={(e) => setEF("vendor", e.target.value)} data-testid="edit-vendor" />
                </div>
                <div>
                  <Label>Storage Location</Label>
                  <Input value={editForm.location} onChange={(e) => setEF("location", e.target.value)} placeholder="e.g. Stores Rack A-3" data-testid="edit-location" />
                </div>
                {branches.length > 0 && (
                  <div className="col-span-2">
                    <Label>Branch</Label>
                    <Select value={editForm.branch_id} onValueChange={(v) => setEF("branch_id", v)}>
                      <SelectTrigger data-testid="edit-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea rows={2} value={editForm.description} onChange={(e) => setEF("description", e.target.value)} data-testid="edit-description" />
                </div>
              </div>
              {!isAdmin && (
                <p className="text-[11px] text-slate-500 italic">Name can only be changed by an administrator.</p>
              )}
              <div className="flex justify-end">
                <Button onClick={saveDetails} disabled={savingEdit} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-details-btn">
                  {savingEdit ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </TabsContent>
          )}

          <TabsContent value="history" className="mt-4">
            {loading ? <div className="text-slate-400 text-sm py-6 text-center">Loading...</div>
              : txns.length === 0 ? <div className="text-slate-400 text-sm py-6 text-center flex flex-col items-center"><History className="w-8 h-8 mb-2 text-slate-300" />No transactions yet</div>
                : (
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-500 text-xs uppercase"><th className="px-2 py-2">Date</th><th className="px-2 py-2">Type</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2">Reason</th><th className="px-2 py-2">By</th></tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {txns.map((t) => (
                          <tr key={t.id}>
                            <td className="px-2 py-2 text-xs">{formatDate(t.date)}</td>
                            <td className="px-2 py-2"><Badge className={t.direction === "in" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>{t.direction === "in" ? "IN" : "OUT"}</Badge></td>
                            <td className={`px-2 py-2 text-right font-mono font-semibold ${t.direction === "in" ? "text-emerald-700" : "text-red-700"}`}>{t.direction === "in" ? "+" : "−"}{t.qty}</td>
                            <td className="px-2 py-2 text-slate-700">{t.reason}</td>
                            <td className="px-2 py-2 text-xs text-slate-500">{t.user_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            }
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ManageCategoriesDialog({ open, onOpenChange, cats, onChanged }) {
  const [name, setName] = useState("");
  const [uom, setUom] = useState("pcs");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/consumables/categories", { name: name.trim(), default_uom: uom });
      toast.success("Category added");
      setName(""); setUom("pcs");
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this category? Existing consumables with this category are not affected.")) return;
    try {
      await api.delete(`/consumables/categories/${id}`);
      toast.success("Removed");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Manage Consumable Categories</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="border border-slate-200 rounded-md">
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {cats.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-xs">No categories yet</div>
              ) : cats.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2" data-testid={`category-row-${c.name}`}>
                  <div>
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">Default UOM: <span className="font-mono">{c.default_uom}</span></div>
                  </div>
                  <button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 p-1.5" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <Label className="text-xs uppercase text-slate-500 tracking-wider">Add New Category</Label>
            <div className="grid grid-cols-6 gap-2 mt-2">
              <div className="col-span-3">
                <Input placeholder="Category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-category-name" />
              </div>
              <div className="col-span-2">
                <Select value={uom} onValueChange={setUom}>
                  <SelectTrigger data-testid="new-category-uom"><SelectValue /></SelectTrigger>
                  <SelectContent>{UOM.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={add} disabled={busy || !name.trim()} className="col-span-1 bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-category-btn">
                Add
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------ */
/* Issue / Receive dialog — top-level entry point with item picker.    */
/* ------------------------------------------------------------------ */
function IssueReceiveDialog({ mode, items, onClose, onDone }) {
  const isReceive = mode === "in";
  const today = new Date().toISOString().slice(0, 10);
  const [pick, setPick] = useState(null); // selected consumable object
  const [q, setQ] = useState("");
  const [assets, setAssets] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    qty: "",
    txn_date: today,
    reason: "",
    // out-only
    linked_asset_id: "",
    issued_to_person: "",
    department: "",
    // in-only
    dc_number: "",
    vendor: "",
    unit_cost: "",
    linked_po_id: "",
    remarks: "",
  });

  useEffect(() => {
    if (!isReceive) {
      api.get("/assets").then(({ data }) => setAssets(data)).catch(() => {});
    } else {
      api.get("/purchase-orders").then(({ data }) => {
        setOpenPOs((data || []).filter((p) =>
          ["issued", "approved", "partially_received"].includes(p.status)
        ));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [isReceive]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items.slice(0, 30);
    return items
      .filter((c) => `${c.code} ${c.name} ${c.category || ""} ${c.vendor || ""}`.toLowerCase().includes(query))
      .slice(0, 30);
  }, [items, q]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = () => {
    if (!pick) return false;
    const qty = Number(form.qty);
    if (!qty || qty <= 0) return false;
    if (!form.reason.trim()) return false;
    if (!isReceive && qty > (pick.current_stock || 0)) return false;
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setBusy(true);
    try {
      const payload = {
        direction: mode,
        qty: Number(form.qty),
        reason: form.reason.trim(),
        txn_date: form.txn_date || undefined,
        remarks: form.remarks || undefined,
      };
      if (isReceive) {
        payload.dc_number = form.dc_number || undefined;
        payload.vendor = form.vendor || undefined;
        payload.unit_cost = form.unit_cost === "" ? undefined : Number(form.unit_cost);
        payload.linked_po_id = form.linked_po_id || undefined;
      } else {
        payload.linked_asset_id = form.linked_asset_id || undefined;
        payload.issued_to_person = form.issued_to_person || undefined;
        payload.department = form.department || undefined;
      }
      await api.post(`/consumables/${pick.id}/adjust`, payload);
      toast.success(isReceive
        ? `Received ${form.qty} ${pick.unit_of_measure} of ${pick.name}`
        : `Issued ${form.qty} ${pick.unit_of_measure} of ${pick.name}`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transaction failed");
    } finally { setBusy(false); }
  };

  const accent = isReceive ? "emerald" : "red";
  const AccentIcon = isReceive ? ArrowDownToLine : ArrowUpFromLine;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid={isReceive ? "receive-stock-dialog" : "issue-stock-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-${accent}-100 text-${accent}-700`}>
              <AccentIcon className="w-4 h-4" />
            </div>
            {isReceive ? "Receive Stock" : "Issue Stock"}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1 — Item picker */}
        {!pick ? (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Search and select the consumable you want to {isReceive ? "receive into" : "issue from"} stock.
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                placeholder="Search by code, name, category, vendor…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                data-testid="txn-item-search"
              />
            </div>
            <div className="border rounded-md max-h-80 overflow-y-auto divide-y divide-slate-100" data-testid="txn-item-list">
              {filtered.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">No matching consumables</div>
              ) : filtered.map((c) => {
                const low = c.reorder_level != null && c.current_stock <= c.reorder_level;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setPick(c)}
                    className="w-full text-left px-3 py-2 hover:bg-[#fcf0f3] flex items-center justify-between gap-3 group"
                    data-testid={`txn-pick-${c.code}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-[#7C1D3A]">{c.code}</span>
                        <span className="text-sm font-medium text-slate-900 truncate">{c.name}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {c.category} · {c.location || "no location"}{c.vendor ? ` · ${c.vendor}` : ""}
                      </div>
                    </div>
                    <div className={`text-right shrink-0 font-mono text-sm ${low ? "text-amber-700" : "text-slate-800"}`}>
                      <div className="font-semibold">{c.current_stock} <span className="text-[10px] text-slate-500">{c.unit_of_measure}</span></div>
                      {low && <div className="text-[10px] font-semibold uppercase tracking-wider">low</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Step 2 — Transaction form */
          <div className="space-y-4">
            {/* Selected item chip */}
            <div className={`flex items-center justify-between gap-3 p-3 rounded-md border bg-${accent}-50/40 border-${accent}-200`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-semibold text-[#7C1D3A] bg-white px-1.5 py-0.5 rounded border border-slate-200">{pick.code}</span>
                  <span className="text-sm font-semibold text-slate-900 truncate">{pick.name}</span>
                  <Badge variant="outline" className="text-[10px]">{pick.category}</Badge>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Current stock: <span className="font-mono font-semibold text-slate-800">{pick.current_stock} {pick.unit_of_measure}</span>
                  {pick.location && <> · {pick.location}</>}
                  {pick.unit_cost != null && <> · {formatINR(pick.unit_cost)}/{pick.unit_of_measure}</>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setPick(null); setQ(""); }}
                className="text-slate-400 hover:text-slate-700 p-1"
                title="Change item"
                data-testid="txn-change-item"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.qty}
                    onChange={(e) => set("qty", e.target.value)}
                    placeholder="0.00"
                    data-testid="txn-qty"
                    className={!isReceive && form.qty && Number(form.qty) > pick.current_stock ? "border-red-300 focus:ring-red-200" : ""}
                  />
                  <span className="text-xs text-slate-500 whitespace-nowrap">{pick.unit_of_measure}</span>
                </div>
                {!isReceive && form.qty && Number(form.qty) > pick.current_stock && (
                  <div className="text-[11px] text-red-600 mt-1">Only {pick.current_stock} available</div>
                )}
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.txn_date}
                  onChange={(e) => set("txn_date", e.target.value)}
                  max={today}
                  data-testid="txn-date"
                />
              </div>

              {/* Receive-specific */}
              {isReceive && (
                <>
                  <div>
                    <Label>DC / Invoice No.</Label>
                    <Input value={form.dc_number} onChange={(e) => set("dc_number", e.target.value)} placeholder="e.g. INV-2312" data-testid="txn-dc-number" />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder={pick.vendor || "Supplier name"} data-testid="txn-vendor" />
                  </div>
                  <div>
                    <Label>Unit Cost (₹)</Label>
                    <Input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} placeholder={pick.unit_cost != null ? String(pick.unit_cost) : "Optional — updates item"} data-testid="txn-unit-cost" />
                  </div>
                  <div>
                    <Label>Link to PO (optional)</Label>
                    <Select value={form.linked_po_id || "none"} onValueChange={(v) => set("linked_po_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="txn-linked-po"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="none">— None —</SelectItem>
                        {openPOs.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.po_number} — {p.vendor_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Issue-specific */}
              {!isReceive && (
                <>
                  <div>
                    <Label>Issued to Person</Label>
                    <Input value={form.issued_to_person} onChange={(e) => set("issued_to_person", e.target.value)} placeholder="Recipient name" data-testid="txn-issued-to" />
                  </div>
                  <div>
                    <Label>Department / Cost Centre</Label>
                    <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Maintenance" data-testid="txn-department" />
                  </div>
                  <div className="col-span-2">
                    <Label>Linked Asset (optional)</Label>
                    <Select value={form.linked_asset_id || "none"} onValueChange={(v) => set("linked_asset_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="txn-linked-asset"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="none">— None —</SelectItem>
                        {assets.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.asset_id} — {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="col-span-2">
                <Label>{isReceive ? "Purpose / Notes" : "Reason for Issue"} <span className="text-red-500">*</span></Label>
                <Input
                  value={form.reason}
                  onChange={(e) => set("reason", e.target.value)}
                  placeholder={isReceive ? "e.g. Regular stock replenishment" : "e.g. Machine cleaning shift-A"}
                  data-testid="txn-reason"
                />
              </div>
              <div className="col-span-2">
                <Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Any additional notes" data-testid="txn-remarks" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          {pick && (
            <Button
              onClick={submit}
              disabled={!canSubmit() || busy}
              className={isReceive ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="txn-submit-btn"
            >
              {busy ? "Processing…" : (isReceive ? "Receive Stock" : "Issue Stock")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Migration: bulk-assign unassigned consumables to a branch (admin). */
/* ------------------------------------------------------------------ */
function BulkAssignBranchDialog({ branches, unassignedCount, onClose, onDone }) {
  const [branchId, setBranchId] = useState(branches.find((b) => b.is_default)?.id || branches[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!branchId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/consumables/bulk-assign-branch", { branch_id: branchId });
      toast.success(`${data.assigned} consumables assigned to ${data.branch}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to assign"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bulk-assign-branch-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Building2 className="w-4 h-4 text-[#7C1D3A]" /> Assign Unassigned Consumables to a Branch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            You currently have <b>{unassignedCount}</b> consumable{unassignedCount === 1 ? "" : "s"} without a branch. Choose which branch should own them. You can move individual items later.
          </p>
          <div>
            <Label>Target Branch</Label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="bulk-assign-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !branchId} className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-assign-confirm-btn">
            {busy ? "Assigning…" : `Assign all ${unassignedCount} items`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------ */
/* Send an item from its current branch to another (approval flow).   */
/* ------------------------------------------------------------------ */
function NewBranchTransferDialog({ consumable, branches, onClose, onDone }) {
  const others = branches.filter((b) => b.id !== consumable.branch_id);
  const [form, setForm] = useState({ to_branch_id: others[0]?.id || "", qty: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const qty = Number(form.qty);
    if (!form.to_branch_id) return toast.error("Choose a destination branch");
    if (!qty || qty <= 0) return toast.error("Quantity must be > 0");
    if (qty > (consumable.current_stock || 0)) return toast.error(`Only ${consumable.current_stock} available`);
    if (!form.reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await api.post("/consumable-branch-transfers", {
        consumable_id: consumable.id,
        to_branch_id: form.to_branch_id,
        qty, reason: form.reason.trim(),
      });
      toast.success("Branch transfer request sent — awaiting approval from destination branch");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="new-branch-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#7C1D3A]" /> Send to Another Branch
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-[#7C1D3A]">{consumable.code}</span>
              <span className="font-medium text-slate-900">{consumable.name}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Available: <span className="font-mono font-semibold text-slate-800">{consumable.current_stock} {consumable.unit_of_measure}</span>
            </div>
          </div>
          <div>
            <Label>Send to</Label>
            <Select value={form.to_branch_id} onValueChange={(v) => setForm({ ...form, to_branch_id: v })}>
              <SelectTrigger data-testid="branch-transfer-to-select"><SelectValue placeholder="Choose destination branch" /></SelectTrigger>
              <SelectContent>
                {others.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quantity <span className="text-red-500">*</span></Label>
            <div className="flex items-center gap-2">
              <Input
                type="number" min="0" step="0.01" value={form.qty}
                onChange={(e) => setForm({ ...form, qty: e.target.value })}
                data-testid="branch-transfer-qty"
              />
              <span className="text-xs text-slate-500">{consumable.unit_of_measure}</span>
            </div>
          </div>
          <div>
            <Label>Reason <span className="text-red-500">*</span></Label>
            <Textarea
              rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Factory ran out this week"
              data-testid="branch-transfer-reason"
            />
          </div>
          <p className="text-[11px] text-slate-500">Stock will move only after the destination branch approves this request.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.to_branch_id || !form.qty || !form.reason}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="branch-transfer-submit-btn"
          >
            {busy ? "Sending…" : "Send Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------ */
/* Branch Transfers list — pending / completed / rejected.            */
/* ------------------------------------------------------------------ */
function BranchTransfersDialog({ branches, items, onClose, onDone }) {
  const [txfrs, setTxfrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", from_branch_id: "", to_branch_id: "", search: "",
  });
  const [exporting, setExporting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/consumable-branch-transfers");
      setTxfrs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyFilters = (list) => list.filter((t) => {
    if (filters.from_branch_id && t.from_branch_id !== filters.from_branch_id) return false;
    if (filters.to_branch_id && t.to_branch_id !== filters.to_branch_id) return false;
    if (filters.date_from && (t.raised_at || "") < filters.date_from) return false;
    if (filters.date_to && (t.raised_at || "").slice(0, 10) > filters.date_to) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hay = `${t.number} ${t.consumable_code} ${t.consumable_name} ${t.reason} ${t.raised_by_name}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const filtered = useMemo(() => applyFilters(txfrs).filter((t) => t.status === tab), [txfrs, tab, filters]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = { ...filters };
      // Strip empty values so the URL stays clean.
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      if (tab) params.status = tab;
      const res = await api.get(`/consumable-branch-transfers.${fmt}`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `branch-transfer-register-${stamp}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally { setExporting(null); }
  };

  const approve = async (t) => {
    if (!window.confirm(`Approve transfer of ${t.qty} ${t.unit_of_measure || ""} of ${t.consumable_name} from ${t.from_branch_name} to ${t.to_branch_name}?`)) return;
    try {
      await api.post(`/consumable-branch-transfers/${t.id}/approve`, {});
      toast.success("Approved — stock moved");
      await load();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/consumable-branch-transfers/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="branch-transfers-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#7C1D3A]" /> Inter-Branch Transfers
          </DialogTitle>
        </DialogHeader>
        {/* Filters + Exports */}
        <div className="flex flex-wrap items-end gap-2 pb-2 border-b border-slate-100 mb-2">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-[10px] uppercase text-slate-500">Search</Label>
            <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Item / BXFR / reason" data-testid="bxfr-filter-search" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">From</Label>
            <Select value={filters.from_branch_id || "any"} onValueChange={(v) => setFilters({ ...filters, from_branch_id: v === "any" ? "" : v })}>
              <SelectTrigger className="min-w-[110px]" data-testid="bxfr-filter-from"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">To</Label>
            <Select value={filters.to_branch_id || "any"} onValueChange={(v) => setFilters({ ...filters, to_branch_id: v === "any" ? "" : v })}>
              <SelectTrigger className="min-w-[110px]" data-testid="bxfr-filter-to"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Date From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="bxfr-filter-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Date To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="bxfr-filter-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setFilters({ date_from: "", date_to: "", from_branch_id: "", to_branch_id: "", search: "" })} data-testid="bxfr-filter-clear">Clear</Button>
          <div className="flex gap-1.5 ml-auto">
            <Button variant="outline" size="sm" onClick={() => doExport("pdf")} disabled={exporting === "pdf"} data-testid="bxfr-export-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> {exporting === "pdf" ? "…" : "PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} disabled={exporting === "xlsx"} data-testid="bxfr-export-xlsx">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {exporting === "xlsx" ? "…" : "Excel"}
            </Button>
          </div>
        </div>
        <div className="flex gap-2 mb-2">
          {["pending", "completed", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setTab(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition border ${tab === s ? "bg-[#7C1D3A] text-white border-[#7C1D3A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#7C1D3A]"}`}
              data-testid={`branch-transfers-tab-${s}`}
            >
              {s} ({applyFilters(txfrs).filter((t) => t.status === s).length})
            </button>
          ))}
        </div>
        <div className="max-h-[420px] overflow-y-auto space-y-2">
          {loading && <div className="text-center text-slate-400 py-6">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">No {tab} transfers</div>}
          {filtered.map((t) => (
            <div key={t.id} className="border rounded-md p-3" data-testid={`branch-transfer-row-${t.number}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-mono font-semibold text-[#7C1D3A]">{t.number}</div>
                <div className="text-[11px] text-slate-500">{t.raised_at ? formatDateTime(t.raised_at) : ""}</div>
              </div>
              <div className="mt-1 text-sm">
                <span className="font-semibold text-slate-900">{t.consumable_name}</span>
                <span className="text-slate-500"> · {t.qty} {t.unit_of_measure || ""}</span>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap">
                <span className="font-medium">{t.from_branch_name}</span>
                <ArrowLeftRight className="w-3 h-3" />
                <span className="font-medium">{t.to_branch_name}</span>
                <span className="text-slate-400">·</span>
                <span>by {t.raised_by_name}</span>
              </div>
              {t.reason && <div className="text-[11px] text-slate-500 italic mt-1">“{t.reason}”</div>}
              {t.rejection_reason && (
                <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-1.5 mt-1">Rejected: {t.rejection_reason}</div>
              )}
              {t.status === "pending" && (
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => approve(t)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`approve-branch-transfer-${t.number}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve & Move Stock
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectFor(t)}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                    data-testid={`reject-branch-transfer-${t.number}`}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
        {rejectFor && (
          <Dialog open onOpenChange={() => setRejectFor(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Reject Transfer {rejectFor.number}</DialogTitle></DialogHeader>
              <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection" data-testid="reject-branch-transfer-reason" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
                <Button onClick={reject} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-branch-transfer-confirm">Confirm Reject</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

